import { motion } from "framer-motion";

const PETAL_COLORS = ["#E58AA5", "#F2A65A", "#E0532F", "#F7C6D9", "#F4B860"];

// Deterministic petal field: spread across the area, staggered falls, varied sizes/speeds
const PETALS = Array.from({ length: 14 }, (_, i) => ({
  left: (i * 71 + 13) % 100,
  delay: (i * 1.9) % 13,
  duration: 9 + (i % 5) * 2.2,
  size: 9 + (i % 4) * 4,
  color: PETAL_COLORS[i % PETAL_COLORS.length],
  sway: 18 + (i % 3) * 14,
  blur: i % 3 === 2,
}));

const IDOL_BASE = { corner: "min(9rem, 26vw)", top_row: "min(6rem, 22vw)" };
const IDOL_SCALE = { "100": 1, "75": 0.75, "50": 0.5 };
const PETAL_EFFECTS = new Set(["drift", "swirl", "shower", "float"]);

const Petals = ({ className, effect }) => (
  <div
    data-testid="festival-petals"
    data-effect={PETAL_EFFECTS.has(effect) ? effect : "drift"}
    aria-hidden="true"
    className={`pointer-events-none absolute overflow-hidden petals-${PETAL_EFFECTS.has(effect) ? effect : "drift"} ${className}`}
  >
    {PETALS.map((p, i) => (
      <span
        key={i}
        className="petal-fall absolute top-0"
        style={{ left: `${p.left}%`, ["--dur"]: `${p.duration}s`, ["--delay"]: `${p.delay}s` }}
      >
        <span
          className="petal-sway block"
          style={{
            width: p.size,
            height: p.size * 1.35,
            background: `linear-gradient(135deg, ${p.color}, ${p.color}CC)`,
            ["--sway"]: `${p.sway}px`,
            filter: p.blur ? "blur(1px)" : undefined,
            opacity: 0.9,
          }}
        />
      </span>
    ))}
  </div>
);

const Idol = ({ src, layout, size }) => {
  const base = IDOL_BASE[layout] || IDOL_BASE.corner;
  const scale = IDOL_SCALE[size] ?? 1;
  return (
    <motion.img
      src={src}
      alt="Lord Ganesha"
      data-testid="festival-image"
      animate={{ y: [0, -6, 0] }}
      transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
      className="arch object-cover shadow-[0_14px_45px_rgba(200,106,81,0.35)] ring-4 ring-white/70"
      style={{ aspectRatio: "3/4", width: `calc(${base} * ${scale})` }}
    />
  );
};

const wishClass =
  "font-serif italic font-semibold leading-snug bg-gradient-to-r from-[#C86A51] via-[#D4A017] to-[#C86A51] bg-clip-text text-transparent drop-shadow-sm";

export const FestivalCorner = ({ content }) => {
  const isTopRow = content?.festival_layout === "top_row";
  const layout = isTopRow ? "top_row" : "corner";
  const wish = content?.festival_wish || "Happy Vinayaka Chavithi";
  const idolSize = content?.festival_idol_size || "100";
  const petalEffect = content?.festival_petal_effect || "drift";

  if (isTopRow) {
    return (
      <motion.div
        data-testid="festival-corner"
        data-layout="top_row"
        initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 border-b border-accent/15 bg-accent/5"
      >
        <div className="mx-auto max-w-6xl px-6 py-5 flex flex-col items-center gap-2.5">
          <Idol src={content.festival_image} layout={layout} size={idolSize} />
          <p data-testid="festival-wish" className={`${wishClass} text-2xl sm:text-3xl md:text-4xl text-center`}>
            {wish}
          </p>
        </div>
        <Petals className="inset-x-0 top-0 z-20 h-[60vh]" effect={petalEffect} />
      </motion.div>
    );
  }

  return (
    <motion.div
      data-testid="festival-corner"
      data-layout="corner"
      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
      className="md:absolute md:top-6 md:left-8 z-10 px-6 pt-4 md:px-0 md:pt-0"
    >
      <div className="flex md:flex-col items-center gap-4 md:gap-2.5">
        <Idol src={content.festival_image} layout={layout} size={idolSize} />
        <p data-testid="festival-wish" className={`${wishClass} text-xl md:text-2xl md:text-center`}>
          {wish}
        </p>
      </div>
      <Petals className="-top-8 left-0 w-[86vw] sm:w-[420px] h-[72vh]" effect={petalEffect} />
    </motion.div>
  );
};
